import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  type SegmentedControlOption,
  Select,
  type SelectOption,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import {
  DateStepper,
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { convertAreaLabel } from '~/models/area'
import { TargetDateSegment } from '~/pages/DemandForecastList/components/TargetDateSegment'
import type { TargetDateSegmentKey } from '~/pages/DemandForecastList/models/targetDateSegment'

/**
 * エリア select の「全エリア」option を表す sentinel 値。 業務エリア (AreaCode enum
 * 値の文字列表現、`'1'`〜`'10'`) と衝突しない文字列を使う (design-note
 * demand-list-area-filter Decision #2: 「全エリア」を明示 option とする)。
 */
const AREA_FILTER_ALL_VALUE = 'ALL'

export type FilterBarProps = {
  /** 現在選択中の対象日 */
  targetDate: Date
  /**
   * エリア select の候補 (design-note demand-list-area-filter Decision #1:
   * 個人設定の業務エリア。 10 エリア北→南順 ∩ 業務エリア、 呼び出し側で導出済み)。
   */
  businessAreas: AreaCode[]
  /** 選択中のエリア (undefined = 全エリア)。 単一選択 (Decision #2)。 */
  selectedArea: AreaCode | undefined
  /** 選択中のステータス (undefined = 全て)。WorkflowCaseStatus で絞り込む。 */
  selectedStatus: WorkflowCaseStatus | undefined
  onTargetDateChange: (date: Date) => void
  /** エリア select の選択変更 (undefined = 「全エリア」選択) */
  onAreaChange: (area: AreaCode | undefined) => void
  onStatusChange: (status: WorkflowCaseStatus | undefined) => void
  /**
   * 対象日 radio (当日/翌日/翌々日/翌々日以降) の現在アクティブな区分。
   * date picker / `<` `>` で選んだ日が該当区分に一致しない場合 (過去日等) は
   * undefined (= 全ボタン非 active)。
   */
  targetDateSegment: TargetDateSegmentKey | undefined
  /** 対象日 radio クリック。対象日の算出は呼び出し側 (操作時点の現在日) に委ねる */
  onTargetDateSegmentChange: (segment: TargetDateSegmentKey) => void
}

export const FilterBar = ({
  targetDate,
  businessAreas,
  selectedArea,
  selectedStatus,
  onTargetDateChange,
  onAreaChange,
  onStatusChange,
  targetDateSegment,
  onTargetDateSegmentChange,
}: FilterBarProps): React.ReactElement => {
  const { t } = useTranslation()

  // spec §7 / design #dfl-status-seg: すべて / 未着手 / 進行中 / 完了 / 削除
  // 実績反映済は #4097 で P0 スコープ外と確定したため含めない
  const statusFilterOptions: Array<SegmentedControlOption<string>> = useMemo(
    () => [
      { value: '', label: t('page:demandForecastList.filter.statusAll') },
      {
        value: String(WorkflowCaseStatus.NOT_STARTED),
        label: t('page:workflowCommon.caseStatus.notStarted'),
      },
      {
        value: String(WorkflowCaseStatus.IN_PROGRESS),
        label: t('page:workflowCommon.caseStatus.inProgress'),
      },
      {
        value: String(WorkflowCaseStatus.COMPLETE),
        label: t('page:workflowCommon.caseStatus.complete'),
      },
      {
        value: String(WorkflowCaseStatus.DELETED),
        label: t('page:workflowCommon.caseStatus.deleted'),
      },
    ],
    [t],
  )

  // convertAreaLabel は i18next.t を引くため、 言語切替 (I18nProvider が session RPC 後に
  // changeLanguage する) に追従できるよう依存に t を入れる (調達計画一覧と同形)。
  // 候補 = 「全エリア」+ 業務エリア (北→南順、 呼び出し側で導出済み。 Decision #1)。
  const areaSelectOptions: SelectOption[] = useMemo(
    () => [
      {
        value: AREA_FILTER_ALL_VALUE,
        label: t('page:demandForecastList.filter.areaAll'),
      },
      ...businessAreas.map((areaCode) => ({
        value: String(areaCode),
        label: convertAreaLabel(areaCode),
      })),
    ],
    [t, businessAreas],
  )
  const selectedAreaValue =
    selectedArea === undefined ? AREA_FILTER_ALL_VALUE : String(selectedArea)
  const selectedAreaOption =
    areaSelectOptions.find((opt) => opt.value === selectedAreaValue) ?? null

  return (
    <FilterToolbar>
      <FilterSection label={t('page:demandForecastList.filter.targetDate')}>
        <Flex alignItems="center" flexWrap="wrap" gap={2}>
          <DateStepper
            dateAriaLabel={t('page:demandForecastList.filter.targetDate')}
            hideTodayButton
            onChange={onTargetDateChange}
            value={targetDate}
          />
          <TargetDateSegment
            onChange={onTargetDateSegmentChange}
            value={targetDateSegment}
          />
        </Flex>
      </FilterSection>

      {/* design `#dfl-status-seg` は select ではなく segmented
          (`design/demand/forecast-list.html`「2026-07-15: select → DS segmented 化」)。 */}
      <FilterSection label={t('page:demandForecastList.filter.status')}>
        <AccessibleSegmentedControl
          aria-label={t('page:demandForecastList.filter.status')}
          name="demand-forecast-status"
          onSelect={(next) => {
            if (next === '') {
              onStatusChange(undefined)
              return
            }
            const num = Number(next)
            if (Number.isNaN(num)) {
              onStatusChange(undefined)
              return
            }
            onStatusChange(num as WorkflowCaseStatus)
          }}
          options={statusFilterOptions}
          selected={selectedStatus === undefined ? '' : String(selectedStatus)}
        />
      </FilterSection>

      {/* design `#dfl-area-ms` は複数選択 (EclMultiSelect) だが spec / mock-app は
          単一選択 (design gap、design-note demand-list-area-filter Decision #2)。
          候補は個人設定の業務エリアに連動 (Decision #1)。 */}
      <FilterSection label={t('page:demandForecastList.filter.area')}>
        <Box minWidth="200px" css={selectFontFix}>
          <Select
            aria-label={t('page:demandForecastList.filter.area')}
            isMulti={false}
            // 一覧の sticky TH に menu が覆われるため body へ portal する。
            menuPortalTarget={document.body}
            onChange={(opt) => {
              if (opt === null) return
              onAreaChange(
                opt.value === AREA_FILTER_ALL_VALUE
                  ? undefined
                  : (Number(opt.value) as AreaCode),
              )
            }}
            options={areaSelectOptions}
            selectedOptions={selectedAreaOption}
            size="md"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
          />
        </Box>
      </FilterSection>
    </FilterToolbar>
  )
}
